import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Tooltip,
  Alert,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Timeline as EdgeIcon,
  ContentCopy as CopyIcon,
  Visibility as ViewIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

function EdgesTab({
  edges, nodes, onAddEdge, onEditEdge, onDeleteEdge,
}) {
  const [selectedEdge, setSelectedEdge] = useState(null);

  // Sort edges in logical order based on actual flow connections
  const sortedEdges = useMemo(() => {
    if (!edges || edges.length === 0) return [];

    // Create a flow graph to determine execution order
    const flowGraph = new Map();
    const inDegree = new Map();

    // Initialize in-degree count for all nodes
    nodes.forEach((node) => {
      inDegree.set(node.id, 0);
    });

    // Build the flow graph and calculate in-degrees
    edges.forEach((edge) => {
      if (!flowGraph.has(edge.source)) {
        flowGraph.set(edge.source, []);
      }
      flowGraph.get(edge.source).push(edge.target);

      // Increment in-degree for target node
      inDegree.set(edge.target, (inDegree.get(edge.target) || 0) + 1);
    });

    // Find nodes with no incoming edges (start nodes)
    const startNodes = [];
    nodes.forEach((node) => {
      if ((inDegree.get(node.id) || 0) === 0) {
        startNodes.push(node.id);
      }
    });

    // Topological sort to determine execution order
    const executionOrder = [];
    const queue = [...startNodes];

    while (queue.length > 0) {
      const current = queue.shift();
      executionOrder.push(current);

      const neighbors = flowGraph.get(current) || [];
      neighbors.forEach((neighbor) => {
        inDegree.set(neighbor, inDegree.get(neighbor) - 1);
        if (inDegree.get(neighbor) === 0) {
          queue.push(neighbor);
        }
      });
    }

    // Sort edges based on execution order
    return [...edges].sort((a, b) => {
      const aSourceIndex = executionOrder.indexOf(a.source);
      const bSourceIndex = executionOrder.indexOf(b.source);

      if (aSourceIndex !== bSourceIndex) {
        return aSourceIndex - bSourceIndex;
      }

      // If same source, sort by target execution order
      const aTargetIndex = executionOrder.indexOf(a.target);
      const bTargetIndex = executionOrder.indexOf(b.target);
      return aTargetIndex - bTargetIndex;
    });
  }, [edges, nodes]);

  // Validate flow connections and detect issues
  const flowIssues = useMemo(() => {
    if (!edges || edges.length === 0) return [];

    const issues = [];

    // Check for missing start connection
    const startNodes = nodes.filter((n) => n.type === 'start');
    const hasStartConnection = edges.some((e) => startNodes.some((s) => s.id === e.source));
    if (startNodes.length > 0 && !hasStartConnection) {
      issues.push({
        type: 'missing_start_connection',
        message: 'Start node(s) have no outgoing connections',
        severity: 'error',
      });
    }

    // Check for orphaned nodes (nodes with no connections)
    const connectedNodes = new Set();
    edges.forEach((edge) => {
      connectedNodes.add(edge.source);
      connectedNodes.add(edge.target);
    });

    nodes.forEach((node) => {
      if (!connectedNodes.has(node.id)) {
        issues.push({
          type: 'orphaned_node',
          message: `Node "${node.data?.label || node.id}" is not connected to any other node`,
          severity: 'warning',
        });
      }
    });

    // Check for cycles (simplified check)
    const visited = new Set();
    const recStack = new Set();

    const hasCycle = (nodeId) => {
      if (recStack.has(nodeId)) return true;
      if (visited.has(nodeId)) return false;

      visited.add(nodeId);
      recStack.add(nodeId);

      const outgoingEdges = edges.filter((e) => e.source === nodeId);
      for (const edge of outgoingEdges) {
        if (hasCycle(edge.target)) return true;
      }

      recStack.delete(nodeId);
      return false;
    };

    // Check for cycles starting from each node
    nodes.forEach((node) => {
      if (!visited.has(node.id) && hasCycle(node.id)) {
        issues.push({
          type: 'cycle_detected',
          message: `Cycle detected in flow starting from "${node.data?.label || node.id}"`,
          severity: 'error',
        });
      }
    });

    return issues;
  }, [edges, nodes]);

  // Generate dynamic flow visualization
  const getFlowVisualization = () => {
    if (!edges || edges.length === 0) return [];

    // Create a map of node connections
    const nodeConnections = new Map();
    edges.forEach((edge) => {
      if (!nodeConnections.has(edge.source)) {
        nodeConnections.set(edge.source, []);
      }
      nodeConnections.get(edge.source).push(edge.target);
    });

    // Find start nodes (nodes with no incoming edges)
    const hasIncoming = new Set();
    edges.forEach((edge) => hasIncoming.add(edge.target));
    const startNodes = nodes.filter((node) => !hasIncoming.has(node.id));

    // Build flow path from start nodes
    const flowPath = [];
    const visited = new Set();

    const traverseFlow = (nodeId, path = []) => {
      if (visited.has(nodeId)) return;
      visited.add(nodeId);

      const node = nodes.find((n) => n.id === nodeId);
      if (node) {
        path.push(node);
      }

      const connections = nodeConnections.get(nodeId) || [];
      if (connections.length === 0) {
        // End of path
        flowPath.push([...path]);
      } else {
        // Continue with first connection (for simplicity)
        traverseFlow(connections[0], path);
      }
    };

    startNodes.forEach((startNode) => {
      traverseFlow(startNode.id);
    });

    return flowPath;
  };

  const getEdgeTypeInfo = (edge) => {
    const edgeType = edge.type || 'edge-basic';
    switch (edgeType) {
      case 'edge-conditional':
        return { color: 'warning', icon: <WarningIcon />, label: 'Conditional' };
      case 'edge-success':
        return { color: 'success', icon: <SuccessIcon />, label: 'Success' };
      case 'edge-error':
        return { color: 'error', icon: <ErrorIcon />, label: 'Error' };
      case 'edge-always':
        return { color: 'primary', icon: <InfoIcon />, label: 'Always' };
      case 'edge-parallel':
        return { color: 'info', icon: <EdgeIcon />, label: 'Parallel' };
      case 'edge-join':
        return { color: 'secondary', icon: <EdgeIcon />, label: 'Join' };
      case 'edge-loop':
        return { color: 'warning', icon: <EdgeIcon />, label: 'Loop' };
      case 'edge-timeout':
        return { color: 'error', icon: <WarningIcon />, label: 'Timeout' };
      case 'edge-retry':
        return { color: 'warning', icon: <WarningIcon />, label: 'Retry' };
      default:
        return { color: 'default', icon: <EdgeIcon />, label: 'Basic' };
    }
  };

  const getSourceNodeName = (edge) => {
    const sourceNode = nodes.find((n) => n.id === edge.source);
    return sourceNode?.data?.label || sourceNode?.id || 'Unknown';
  };

  const getTargetNodeName = (edge) => {
    const targetNode = nodes.find((n) => n.id === edge.target);
    return targetNode?.data?.label || targetNode?.id || 'Unknown';
  };

  const handleCopyEdge = (edge) => {
    const edgeData = JSON.stringify(edge, null, 2);
    navigator.clipboard.writeText(edgeData);
    // You could add a snackbar notification here
  };

  const handleViewEdge = (edge) => {
    setSelectedEdge(edge);
    // You could show a detailed view dialog here
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2,
      }}
      >
        <Typography variant="h6">Flow Connections</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAddEdge}
        >
          Add Connection
        </Button>
      </Box>

      {edges.length === 0 ? (
        <Paper sx={{ flex: 1, p: 2, overflow: 'auto' }}>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <EdgeIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No connections defined
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add connections between nodes to define your flow logic
            </Typography>
            <Button variant="contained" onClick={onAddEdge}>
              Create First Connection
            </Button>
          </Box>
        </Paper>
      ) : (
        <>
          {/* Flow Validation Alerts */}
          {flowIssues.length > 0 && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" color="error" gutterBottom>
                ⚠️ Flow Issues Detected
              </Typography>
              {flowIssues.map((issue, index) => (
                <Alert
                  key={index}
                  severity={issue.severity}
                  sx={{ mb: 1 }}
                >
                  {issue.message}
                </Alert>
              ))}
            </Paper>
          )}

          {/* Dynamic Flow Visualization */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              🔄 Current Flow Path
            </Typography>
            {getFlowVisualization().length > 0 ? (
              getFlowVisualization().map((path, pathIndex) => (
                <Box key={pathIndex} sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Path
                    {' '}
                    {pathIndex + 1}
                    :
                  </Typography>
                  <Box sx={{
                    display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center',
                  }}
                  >
                    {path.map((node, index) => (
                      <Box key={node.id} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Chip
                          label={node.data?.label || node.id}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                        {index < path.length - 1 && (
                          <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>
                            →
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No flow paths detected. Add connections between nodes to see the flow.
              </Typography>
            )}
          </Paper>

          {/* Edge Statistics */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Total Connections:
                {' '}
                {edges.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Conditional:
                {' '}
                {edges.filter((e) => e.type === 'edge-conditional').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Success:
                {' '}
                {edges.filter((e) => e.type === 'edge-success').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Error:
                {' '}
                {edges.filter((e) => e.type === 'edge-error').length}
              </Typography>
            </Stack>
          </Paper>

          {/* Edges List */}
          <Paper sx={{ flex: 1, p: 2, overflow: 'auto' }}>
            <List>
              {sortedEdges.map((edge) => {
                const typeInfo = getEdgeTypeInfo(edge);
                const sourceName = getSourceNodeName(edge);
                const targetName = getTargetNodeName(edge);

                return (
                  <ListItem
                    key={edge.id}
                    divider
                    sx={{
                      border: selectedEdge?.id === edge.id ? '2px solid' : 'none',
                      borderColor: 'primary.main',
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                    onClick={() => setSelectedEdge(edge)}
                  >
                    <ListItemText
                      primary={(
                        <Box sx={{
                          display: 'flex', alignItems: 'center', gap: 1, mb: 1,
                        }}
                        >
                          <Chip
                            label={sourceName}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: 'primary.main',
                            fontWeight: 'bold',
                            fontSize: '1.2rem',
                          }}
                          >
                            →
                          </Box>
                          <Chip
                            label={targetName}
                            size="small"
                            variant="outlined"
                            color="secondary"
                          />
                          <Chip
                            label={typeInfo.label}
                            size="small"
                            color={typeInfo.color}
                            icon={typeInfo.icon}
                          />
                          {edge.label && (
                            <Chip
                              label={edge.label}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          )}
                        </Box>
                      )}
                      secondary={(
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            ID:
                            {' '}
                            {edge.id}
                          </Typography>
                          {edge.condition && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              Condition:
                              {' '}
                              {edge.condition}
                            </Typography>
                          )}
                          {edge.description && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {edge.description}
                            </Typography>
                          )}
                        </Box>
                      )}
                    />
                    <ListItemSecondaryAction>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewEdge(edge)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Copy Edge">
                          <IconButton
                            size="small"
                            onClick={() => handleCopyEdge(edge)}
                          >
                            <CopyIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Edge">
                          <IconButton
                            size="small"
                            onClick={() => onEditEdge(edge)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Edge">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onDeleteEdge(edge.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        </>
      )}
    </Box>
  );
}

export default EdgesTab;
